import { computed } from 'vue'
import { useProjectStore } from '@/stores/project'
import { useValidator, type ValidationSchema } from '@/validation/form-validator'
import type { NavigationGuardNext } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { scrollToElement } from '@/helpers/shared'
import { useProjectCalculator } from '@/stores/projectCenter/projectCalculator'
import { useRouter } from 'vue-router'
import { Events, useEventBus, DialogEvent, useDialogBus } from '@/helpers/event-bus'
import type { DefaultObject, NestedObject } from '@/types'
import type { UpdateDataType } from '@/components/partials/display/VersionUpdate.vue'

export interface ValidationConfig {
  schema: ValidationSchema
  fieldSections?: Record<string, string> | (() => Record<string, string>) // field name -> section selector
  sectionErrors?: Record<string, string> | (() => Record<string, string>) // section selector -> error i18n key
}

export default {
  useProjectPageBase(validationConfig?: ValidationConfig) {
    const { t } = useI18n()
    const projectStore = useProjectStore()
    const calculator = useProjectCalculator()
    const eventBus = useEventBus()
    const dialogBus = useDialogBus()
    const project: DefaultObject | null = computed(() => projectStore.getProject)
    const projectMeta: DefaultObject | null = computed(() => projectStore.getMeta)
    const projectValidRoles = computed(() => projectStore.getValidRoles)
    const projectName = computed(() => projectStore.getMeta?.title)
    const availableUpdates = computed(() => projectStore.getAvailableUpdates)
    const enrichedUpdates = computed(() => projectStore.getEnrichedUpdates)
    const highestUpdateLevel = computed(() => projectStore.getHighestUpdateLevel)

    const initCalculators = function () {
      calculator.setProject(project)
    }

    const validator = useValidator()
    const validationSchema = validationConfig?.schema ?? {}

    const router = useRouter()
    function findCurrentSectionIndex(): number {
      const sections = document.querySelectorAll('section.core-container')
      const viewportMiddle = window.innerHeight / 2
      let currentIndex = 0

      sections.forEach((el, index) => {
        const box = el.getBoundingClientRect()
        if (box.y < viewportMiddle) {
          currentIndex = index
        }
      })

      return currentIndex
    }

    async function checkFormIsDirty(
      next: NavigationGuardNext,
      onSaveData: { type: string; data: Record<string, unknown> | undefined }
    ): Promise<void> {
      if (validator.isDirty) {
        const confirmed = await checkLeaveDialog()
        if (confirmed) {
          const success = await saveProjectPart(onSaveData.type, onSaveData.data)
          if (success) {
            next()
            return
          } else {
            next(false)
            return
          }
        }
      }
      next()
    }

    async function checkLeaveDialog(): Promise<boolean> {
      return new Promise<boolean>((resolve) => {
        dialogBus.emit(DialogEvent.SHOW_CONFIRM, {
          title: t('general.navigate_from.title'),
          text: t('general.navigate_from.message'),
          confirm: t('general.navigate_from.yes'),
          cancel: t('general.navigate_from.no'),
        })
        dialogBus.on(DialogEvent.DONE, async (confirmed: boolean) => {
          dialogBus.off(DialogEvent.DONE)
          return resolve(confirmed)
        })
      })
    }

    async function validateFormAndShowErrors(): Promise<boolean> {
      if (!validator.validate()) {
        const fieldSectionsConfig = validationConfig?.fieldSections
        const sectionErrorsConfig = validationConfig?.sectionErrors

        // Support both static objects and functions for dynamic sections
        const fieldSections = typeof fieldSectionsConfig === 'function' ? fieldSectionsConfig() : fieldSectionsConfig
        const sectionErrors = typeof sectionErrorsConfig === 'function' ? sectionErrorsConfig() : sectionErrorsConfig

        if (fieldSections && sectionErrors) {
          // Find the first failed field and its section
          const errorKeys = Object.keys(validator.errors.value)
          for (const key of errorKeys) {
            // Clean array keys (e.g., 'usageCorrections.0.manualArea' -> 'usageCorrections[].manualArea')
            const cleanKey = key.replace(/\.\d+\./g, '[].')
            const sectionId = fieldSections[cleanKey]
            if (sectionId) {
              const errorKey = sectionErrors[sectionId]
              if (errorKey) {
                eventBus.emit(Events.SHOW_ERROR, { key: errorKey })
              }
              const section = document.querySelector(sectionId)
              if (section) {
                scrollToElement(section)
              }
              break
            }
          }
        }
        return false
      }
      return true
    }

    async function onNextStepClick(fromSection?: Element): Promise<void> {
      const sections = Array.from(document.querySelectorAll('section.core-container'))
      let currentIndex: number
      if (fromSection) {
        currentIndex = sections.indexOf(fromSection)
      } else {
        currentIndex = findCurrentSectionIndex()
      }
      const nextSection = sections[currentIndex + 1]
      if (nextSection) {
        scrollToElement(nextSection)
      }
    }

    async function onNextModuleClick(
      type: string,
      data: Record<string, unknown> | undefined,
      nextModule: string,
      params?: Record<string, string | number>,
      role?: string
    ): Promise<void> {
      if (await validateFormAndShowErrors()) {
        const success = await saveProjectPart(type, data)
        if (success) {
          // Find the index of the role to maintain role context across navigation
          let routeParams = params
          if (role) {
            const roleIndex = projectValidRoles.value.indexOf(role)
            if (roleIndex !== -1) {
              routeParams = { ...params, currentTabIndex: roleIndex }
            }
          }
          await router.push({ name: nextModule, params: routeParams })
          return
        }
      }
    }

    async function saveProjectPart(type: string, data: Record<string, unknown> | undefined): Promise<boolean> {
      if (!data) return false
      let success = false
      if (!(await validateFormAndShowErrors())) {
        return false
      }
      success = await projectStore.updateProjectPart(type, data)
      if (success) {
        if (type === 'general') {
          calculator.setProjectGeneralData(data as NestedObject)
        }
        validator.initForm(data, validationSchema)
        projectStore.persistClientSave()
        return true
      } else {
        return false
      }
    }

    async function viewOldSummary() {
      projectStore.setUseStoredResults(true)
      await router.push({ name: 'summary-project-view' })
    }

    async function updateVersion(update: UpdateDataType) {
      if (!(await validateFormAndShowErrors())) {
        return false
      }
      projectStore.versionUpdate(update.part, update.version)
      initCalculators()
    }

    async function ignoreVersion(update: UpdateDataType) {
      if (!(await validateFormAndShowErrors())) {
        return false
      }
      projectStore.versionIgnore(update.part, update.version)
      return true
    }

    return {
      validator,
      project,
      initCalculators,
      projectMeta,
      projectValidRoles,
      projectName,
      validationSchema,
      calculator,
      checkFormIsDirty,
      saveProjectPart,
      onNextStepClick,
      onNextModuleClick,
      availableUpdates,
      enrichedUpdates,
      highestUpdateLevel,
      viewOldSummary,
      updateVersion,
      ignoreVersion,
    }
  },
}
